import { BoardNodeHandles } from "./board-node-handles";
import { NodeResizer, type NodeProps } from "@xyflow/react";
import { useCanvasEditability, useCanvasEditSession, useCanvasResizeSession } from "../../lib/canvas-editability";
import { patchNodeData } from "../../lib/canvas-commands";
import type { OrgChartCardNodeData } from "../../types/board";

export function OrgChartCardNode({ id, data, selected, isConnectable }: NodeProps) {
  const nodeData = data as unknown as OrgChartCardNodeData;
  const { canEditDocument } = useCanvasEditability();
  const edit = useCanvasEditSession("text");
  const resize = useCanvasResizeSession();
  const updateField = (field: string, value: string) => {
    if (value !== (data[field] ?? "")) edit.update(patchNodeData(id, { [field]: value }));
  };

  const initials = (nodeData.name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      <NodeResizer
        {...resize}
        isVisible={!!selected && canEditDocument}
        minWidth={160}
        minHeight={60}
        lineClassName="!border-primary"
        handleClassName="!h-2.5 !w-2.5 !rounded-sm !border-2 !border-primary !bg-background"
      />
      <BoardNodeHandles type="orgChartCard" isConnectable={isConnectable} />
      <div
        className="h-full w-full overflow-hidden rounded-lg border-2 bg-card shadow-md"
        style={{
          borderColor: selected ? "var(--color-primary)" : (nodeData.bgColor || "var(--color-border)"),
        }}
        onDoubleClick={edit.begin}
      >
        {/* Color header bar */}
        <div
          className="h-2"
          style={{ backgroundColor: nodeData.bgColor || "#3b82f6" }}
        />

        <div className="flex items-start gap-3 p-3">
          {/* Avatar */}
          {nodeData.avatarUrl ? (
            <img
              src={nodeData.avatarUrl}
              alt={nodeData.name}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: nodeData.bgColor || "#3b82f6" }}
            >
              {initials}
            </div>
          )}

          {/* Info */}
          <div className="min-w-0 flex-1">
            {edit.active ? (
              <div className="space-y-1" onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as globalThis.Node | null)) edit.finish();
              }}>
                <input
                  autoFocus
                  className="nodrag nowheel w-full bg-transparent text-sm font-semibold outline-none"
                  placeholder="Name"
                  defaultValue={nodeData.name}
                  onBlur={(e) => updateField("name", e.target.value)}
                />
                <input
                  className="nodrag nowheel w-full bg-transparent text-xs text-muted-foreground outline-none"
                  placeholder="Title"
                  defaultValue={nodeData.title}
                  onBlur={(e) => updateField("title", e.target.value)}
                />
                <input
                  className="nodrag nowheel w-full bg-transparent text-xs text-muted-foreground outline-none"
                  placeholder="Department"
                  defaultValue={nodeData.department}
                  onBlur={(e) => {
                    updateField("department", e.target.value);
                  }}
                />
              </div>
            ) : (
              <>
                <p className="truncate text-sm font-semibold">{nodeData.name || "Name"}</p>
                <p className="truncate text-xs text-muted-foreground">{nodeData.title || "Title"}</p>
                {nodeData.department && (
                  <p className="truncate text-xs text-muted-foreground">{nodeData.department}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
